import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { initialAbility } from '@/libs/acl/config'
import { resetFcmRegistrationCache } from '@/utils/fcm-push'
import callAnalyticsService from '@/services/call-analytics-service'

export function isAuthHttpStatus(status) {
  return status === 401 || status === 403
}

/**
 * Extract HTTP auth status from a service response or axios error.
 */
export function getAuthHttpStatus(resOrErr) {
  if (!resOrErr) return null

  const fromAxios = resOrErr.response?.status
  if (fromAxios) return fromAxios

  const candidates = [resOrErr.statusCode, resOrErr.httpStatus, resOrErr.status]
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i]
    if (typeof value === 'number' && isAuthHttpStatus(value)) return value
  }

  return null
}

function getAuthFailureMessage(resOrErr) {
  return (
    resOrErr?.message
    || resOrErr?.response?.data?.message
    || ''
  ).toLowerCase()
}

/**
 * True when the payload represents an expired/invalid session rather than a
 * normal business-rule failure.
 */
export function isAuthFailure(resOrErr) {
  const status = getAuthHttpStatus(resOrErr)
  if (status === 401) return true

  const message = getAuthFailureMessage(resOrErr)
  if (message === 'unauthorized') return true

  if (status === 403) {
    return message === 'forbidden'
      || /token|session|expired|not authenticated|login again/.test(message)
  }

  return false
}

let sessionExpiryHandled = false

/**
 * Show a friendly session-expired message, clear auth state, and redirect to login.
 * Matches UserDropdown / PunchAttendance / InvoiceDashboard logout patterns.
 */
export function handleSessionExpiry(vm) {
  if (sessionExpiryHandled) return true
  sessionExpiryHandled = true

  window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])

  if (vm && vm.$toast) {
    vm.$toast({
      component: ToastificationContent,
      props: {
        title: 'Session Expired',
        text: 'Your session has expired. Please log in again.',
        icon: 'AlertTriangleIcon',
        variant: 'warning',
      },
    })
  }

  resetFcmRegistrationCache()
  if (callAnalyticsService && typeof callAnalyticsService.clearSessionCaches === 'function') {
    callAnalyticsService.clearSessionCaches()
  }
  localStorage.clear()

  if (vm && vm.$ability) {
    vm.$ability.update(initialAbility)
  }

  if (vm && vm.$router) {
    vm.$router.push({ name: 'auth-login' })
  }

  return true
}

/** Allow a fresh session-expiry flow after the user logs in again. */
export function resetSessionExpiryHandled() {
  sessionExpiryHandled = false
}
